import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranscriptionJobs } from "../contexts/TranscriptionContext";
import type { TranscriptionJob } from "../contexts/TranscriptionContext";
import { computeProgress } from "../lib/transcriptionProgress";
import { displayTitle } from "@/lib/displayTitle";

const MAX_EXPANDED = 3;
const GREEN_FLASH_MS = 2000;
const COLLAPSE_MS = 300;

// ── Notification popup (top-right toast) ──

interface Notification {
  id: string;
  title: string;
  resultItemId?: string;
}

function NotificationPopup({
  notification,
  onDismiss,
  onView,
}: {
  notification: Notification;
  onDismiss: () => void;
  onView: () => void;
}) {
  useEffect(() => {
    const t = setTimeout(onDismiss, 6000);
    return () => clearTimeout(t);
  }, [onDismiss]);

  const handleClick = () => {
    onView();
  };

  return (
    <div
      onClick={handleClick}
      className="animate-in slide-in-from-right fade-in pointer-events-auto flex w-full max-w-80 cursor-pointer items-start gap-3 rounded-lg border border-gray-200 bg-white p-3 shadow-lg transition-colors hover:bg-gray-50 sm:w-80"
    >
      <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-green-100">
        <svg className="h-3.5 w-3.5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-gray-900">
          {displayTitle(notification.title)}
        </p>
        <p className="text-xs text-gray-500">Transcription complete — click to view</p>
      </div>
      <button
        onClick={(e) => {
          e.stopPropagation();
          onDismiss();
        }}
        className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600"
        aria-label="Dismiss notification"
      >
        <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}

// ── Status icon ──

function StatusIcon({ status }: { status: TranscriptionJob["status"] }) {
  if (status === "completed") {
    return (
      <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-green-100">
        <svg className="h-3 w-3 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      </div>
    );
  }
  if (status === "failed") {
    return (
      <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-red-100">
        <svg className="h-3 w-3 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </div>
    );
  }
  return <div className="h-2 w-2 shrink-0 animate-pulse rounded-full bg-blue-500" />;
}

function progressColor(status: TranscriptionJob["status"]) {
  if (status === "completed") return "bg-green-500";
  if (status === "failed") return "bg-red-400";
  return "bg-blue-500";
}

function stageLabel(job: TranscriptionJob) {
  if (job.status === "completed") return "Done";
  if (job.status === "failed") return job.error || "Failed";
  return job.stage;
}

function stageColor(status: TranscriptionJob["status"]) {
  if (status === "completed") return "text-green-600";
  if (status === "failed") return "text-red-500";
  return "text-gray-500";
}

// ── Single job bar ──

function JobBar({
  job,
  collapsing,
  onHide,
  onClick,
}: {
  job: TranscriptionJob;
  collapsing: boolean;
  onHide: () => void;
  onClick: () => void;
}) {
  const handleTap = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onClick();
  };

  // Polling jobs: render-time time-based estimate (Bug 8 fix no stage-floor jumps).
  // Completed/failed jobs: use the stored progress (100 / 0).
  const displayProgress =
    job.status === "polling"
      ? computeProgress(job.startedAt, job.estimatedDurationSec)
      : job.progress;

  return (
    <div
      className={`border-t border-gray-100 first:border-t-0 transition-all duration-300 overflow-hidden bg-white ${
        collapsing ? "max-h-0 opacity-0" : "max-h-16 opacity-100"
      }`}
    >
      <div className="mx-auto flex h-12 max-w-7xl items-center gap-3 px-4 sm:px-6">
        <button
          onClick={handleTap}
          onTouchEnd={handleTap}
          className="flex min-w-0 flex-1 items-center gap-3 text-left touch-manipulation min-h-[44px]"
        >
          <StatusIcon status={job.status} />
          <p className="min-w-0 flex-1 truncate text-sm font-medium text-gray-900">
            {displayTitle(job.title)}
          </p>
          <span className={`hidden shrink-0 text-sm font-medium sm:inline ${stageColor(job.status)}`}>
            {stageLabel(job)}
          </span>
          <div className="flex w-28 shrink-0 items-center gap-2 sm:w-40">
            <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-gray-100">
              <div
                className={`h-full rounded-full transition-all duration-500 ease-out ${progressColor(job.status)}`}
                style={{ width: `${displayProgress}%` }}
              />
            </div>
            <span className="w-8 text-right text-sm tabular-nums text-gray-500">
              {displayProgress}%
            </span>
          </div>
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onHide();
          }}
          className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
          aria-label="Hide"
        >
          <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
}

// ── Main component ──

const AUTO_COLLAPSE_MS = 3000;

export default function TranscriptionBottomBar() {
  const { jobs, hideJob, removeJob } = useTranscriptionJobs();
  const navigate = useNavigate();

  // IDs currently in the "green flash → collapse" animation
  const [collapsingIds, setCollapsingIds] = useState<Set<string>>(new Set());
  // Notifications shown as top-right popups
  const [notifications, setNotifications] = useState<Notification[]>([]);
  // Track which job IDs we've already started collapsing (to avoid re-triggering)
  const processedRef = useRef<Set<string>>(new Set());
  // Collapse/expand state — default collapsed
  const [isCollapsed, setIsCollapsed] = useState(true);
  const autoCollapseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevJobCount = useRef(jobs.length);

  // Auto-expand when a new job is added, then collapse after 3s
  useEffect(() => {
    if (jobs.length > prevJobCount.current) {
      setIsCollapsed(false);
      if (autoCollapseTimer.current) clearTimeout(autoCollapseTimer.current);
      autoCollapseTimer.current = setTimeout(() => setIsCollapsed(true), AUTO_COLLAPSE_MS);
    }
    prevJobCount.current = jobs.length;
  }, [jobs.length]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (autoCollapseTimer.current) clearTimeout(autoCollapseTimer.current);
    };
  }, []);

  // When a job completes or fails: green flash for 2s, then collapse for 300ms, then remove
  useEffect(() => {
    jobs.forEach((job) => {
      if (
        (job.status === "completed" || job.status === "failed") &&
        !job.hidden &&
        !processedRef.current.has(job.jobId)
      ) {
        processedRef.current.add(job.jobId);

        // Show green flash for GREEN_FLASH_MS, then start collapse
        setTimeout(() => {
          setCollapsingIds((prev) => new Set(prev).add(job.jobId));

          // After collapse animation finishes, fire notification and clean up
          setTimeout(() => {
            setCollapsingIds((prev) => {
              const next = new Set(prev);
              next.delete(job.jobId);
              return next;
            });

            // Show popup notification for completed jobs
            if (job.status === "completed") {
              setNotifications((prev) => [
                ...prev,
                {
                  id: job.jobId,
                  title: job.title,
                  resultItemId: job.resultItemId,
                },
              ]);
            }

            // Hide from bottom bar but keep in context for /transcription page
            // (completed jobs will be auto-cleaned after 5 minutes by context)
            hideJob(job.jobId);
          }, COLLAPSE_MS);
        }, GREEN_FLASH_MS);
      }
    });
  }, [jobs, removeJob]);

  const dismissNotification = useCallback((id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  // Visible bars: not hidden, not yet removed
  const visible = jobs.filter((j) => !j.hidden && j.status === "polling");
  // Also include completed/failed that haven't been collapsed yet
  const doneVisible = jobs.filter(
    (j) => !j.hidden && (j.status === "completed" || j.status === "failed") && !collapsingIds.has(j.jobId),
  );
  // Collapsing bars (still render them for the animation)
  const collapsing = jobs.filter(
    (j) => !j.hidden && collapsingIds.has(j.jobId),
  );

  const allBars = [...visible, ...doneVisible, ...collapsing];
  // Sort: most recent first
  allBars.sort((a, b) => b.addedAt - a.addedAt);

  // Active (polling) bars — for compaction logic
  const activeJobs = allBars.filter((j) => j.status === "polling");
  const showCompact = activeJobs.length > MAX_EXPANDED;
  const expandedActive = showCompact ? activeJobs.slice(0, MAX_EXPANDED) : activeJobs;
  const overflowCount = showCompact ? activeJobs.length - MAX_EXPANDED : 0;

  // Done/collapsing bars always show (they auto-remove)
  const doneAndCollapsing = allBars.filter((j) => j.status !== "polling");

  // Combined render list: done bars first (they collapse away), then expanded active
  const renderBars = [...doneAndCollapsing, ...expandedActive];

  const hasAnything = renderBars.length > 0 || overflowCount > 0 || notifications.length > 0;
  if (!hasAnything) return null;

  const totalPolling = activeJobs.length;

  const handleBarClick = (job: TranscriptionJob) => {
    if (job.status === "completed" && job.resultItemId) {
      navigate(`/transcript/${job.resultItemId}`);
    } else if (job.status === "completed") {
      // Completed but no saved item (guest or save failed) - show job result
      navigate(`/transcription/${job.jobId}`);
    } else if (job.status === "polling") {
      // Navigate to dedicated transcription page for this job
      navigate(`/transcription/${job.jobId}`);
    }
  };

  const toggleCollapse = () => {
    setIsCollapsed((prev) => !prev);
    // Cancel any pending auto-collapse when user manually toggles
    if (autoCollapseTimer.current) {
      clearTimeout(autoCollapseTimer.current);
      autoCollapseTimer.current = null;
    }
  };

  const hasBars = renderBars.length > 0 || overflowCount > 0;

  return (
    <>
      {/* Notification popups — top-right */}
      {notifications.length > 0 && (
        <div className="pointer-events-none fixed right-4 top-16 z-[60] flex flex-col gap-2">
          {notifications.map((n) => (
            <NotificationPopup
              key={n.id}
              notification={n}
              onDismiss={() => dismissNotification(n.id)}
              onView={() => {
                dismissNotification(n.id);
                if (n.resultItemId) {
                  navigate(`/transcript/${n.resultItemId}`);
                } else {
                  // No saved item (guest) - navigate to job result page
                  navigate(`/transcription/${n.id}`);
                }
              }}
            />
          ))}
        </div>
      )}

      {/* Bottom bar */}
      {hasBars && (
        <div
          className="fixed inset-x-0 bottom-0 z-50 bg-white shadow-[0_-2px_10px_rgba(0,0,0,0.06)]"
          style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        >
          {isCollapsed ? (
            /* ── Collapsed: single thin summary bar ── */
            <button
              onClick={toggleCollapse}
              className="flex w-full items-center gap-3 bg-blue-50 px-4 py-2 text-left sm:px-6"
            >
              {/* Chevron up */}
              <svg className="h-4 w-4 shrink-0 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
              </svg>
              <div className="h-2 w-2 shrink-0 animate-pulse rounded-full bg-blue-500" />
              <span className="text-sm font-medium text-blue-600">
                {totalPolling} transcription{totalPolling !== 1 ? "s" : ""} in progress
              </span>
            </button>
          ) : (
            /* ── Expanded: full bar stack ── */
            <>
              {/* Collapse toggle header */}
              <button
                onClick={toggleCollapse}
                className="flex w-full items-center gap-2 border-b border-blue-100 bg-blue-50 px-4 py-1.5 sm:px-6"
              >
                <svg className="h-4 w-4 shrink-0 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                </svg>
                <span className="text-sm font-medium text-blue-600">
                  {totalPolling} transcription{totalPolling !== 1 ? "s" : ""} in progress
                </span>
              </button>

              {/* Compact overflow summary */}
              {overflowCount > 0 && (
                <div className="border-b border-gray-100 px-4 py-1.5 text-center sm:px-6">
                  <span className="text-sm font-medium text-gray-500">
                    <span className="mr-1">&#9203;</span>
                    {overflowCount} more transcription{overflowCount > 1 ? "s" : ""} in progress...
                  </span>
                </div>
              )}

              {/* Bars — rendered bottom-up (most recent at bottom of viewport) */}
              {[...renderBars].reverse().map((job) => (
                <JobBar
                  key={job.jobId}
                  job={job}
                  collapsing={collapsingIds.has(job.jobId)}
                  onClick={() => handleBarClick(job)}
                  onHide={() => hideJob(job.jobId)}
                />
              ))}
            </>
          )}
        </div>
      )}
    </>
  );
}

// Export the visible bar height for spacer calculation
// Returns a conservative estimate — collapsed bar is ~36px, expanded varies
export function useBottomBarHeight(jobs: TranscriptionJob[]): number {
  const visible = jobs.filter((j) => !j.hidden);
  const hasAny = visible.some((j) => j.status === "polling" || j.status === "completed" || j.status === "failed");
  // Default collapsed: single thin bar
  return hasAny ? 36 : 0;
}
